import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import CachedIcon from '@mui/icons-material/Cached';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import CardContent from '@mui/material/CardContent';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import ImageUploading from 'react-images-uploading';
import { useRequest, useThemeMediaQuery } from '../../hooks';
import ImageModel from './model/ImageModel';

const funcPrototype = () => new Promise();

const getDefaults = () => ({
  title: 'title',
  path: 'fullPathThumb',
});

/**
 * Componente para cargar y administrar imágenes. Proporciona funcionalidades para cargar, eliminar
 * y validar imágenes desde la computadora o una galería. Utiliza `react-images-uploading` para gestionar
 * la subida de imágenes y `@mui/material` para los estilos y componentes de interfaz.
 *
 * @component
 * @param {string} baseUri - El URI base para la imagen cargada.
 * @param {Function} setImage - Función para establecer la imagen cargada.
 * @param {number} maxSize - Tamaño máximo permitido para la imagen en KB.
 * @param {Object} imageEqualsTo - Parámetros para comparar la imagen cargada.
 * @param {Function} storeRequest - Función de solicitud para almacenar la imagen.
 * @param {Function} deleteRequest - Función de solicitud para eliminar la imagen.
 * @param {Object} formDataParams - Parámetros adicionales para enviar junto con la imagen.
 * @param {boolean} hasGallery - Indica si se debe mostrar una opción para seleccionar desde una galería.
 * @param {boolean} replaceOnly - Indica si solo se permite reemplazar la imagen sin eliminarla.
 * @param {Function} onDeleteUpload - Función que se ejecuta después de eliminar una imagen.
 * @param {Function} onBeforeUpload - Función que se ejecuta antes de subir la imagen.
 * @param {Function} onAfterUpload - Función que se ejecuta después de que se ha subido la imagen.
 * @param {Function} onDirty - Función que se ejecuta cuando el valor de la imagen ha cambiado.
 * @param {Array<string>} acceptType - Tipos de archivo aceptados para la carga (por defecto: `['jpg', 'jpeg', 'png']`).
 * @param {Function} onMessage - Función para mostrar mensajes durante la carga o eliminación de imágenes.
 * @param {String} fallbackMessage - Mensaje en caso de que la imagen haya fallado al renderizar
 *
 * @returns {JSX.Element} El componente de cargador de imágenes.
 */
export const ImagePicker = forwardRef(
  (
    {
      baseUri,
      setImage,
      maxSize,
      imageEqualsTo,
      storeRequest,
      deleteRequest,
      formDataParams,
      hasGallery = false,
      replaceOnly = false,
      onDeleteUpload = () => {},
      onBeforeUpload = () => {},
      onAfterUpload = () => {},
      onDirty = () => {},
      acceptType = ['jpg', 'jpeg', 'png'],
      onMessage = () => {},
      fallbackMessage = 'No fue posible cargar la imagen :(',
    },
    ref
  ) => {
    const storeWrapper = typeof storeRequest === 'function' ? storeRequest : funcPrototype;
    const deleteWrapper = typeof storeRequest === 'function' ? deleteRequest : funcPrototype;
    const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [images, setImages] = useState([]);
    const [canvasImage, setCanvasImage] = useState(undefined);
    const [imageObject, setImageObject] = useState(undefined);
    const [imagePath, setImagePath] = useState(undefined);
    const [imageName, setImageName] = useState(undefined);
    const [wasSavedToServer, setWasSavedToServer] = useState(false);

    const {
      makeRequest: storeImage,
      isLoading: isLoadingStore,
      response: responseStore,
      errorResponse,
      status: statusStore,
    } = useRequest(storeWrapper);

    const { makeRequest: deleteImage } = useRequest(deleteWrapper);

    useEffect(() => {
      if (!setImage) {
        return;
      }
      setImageObject(setImage);
      setWasSavedToServer(true);
    }, [setImage]);

    const handleOnChange = (imageList, addUpdateIndex) => {
      console.log('Uploading...');
      setImages(imageList);
      setCanvasImage(imageList[0]);
    };

    const handleClearAll = () => {
      setCanvasImage(undefined);
      setImages([]);
      setImageObject(undefined);
      setWasSavedToServer(false);
      setImagePath(undefined);
      setImageName(undefined);
    };

    const handleUpload = async (image) => {
      if (typeof onBeforeUpload === 'function') {
        onBeforeUpload(image);
      }
      const formData = new FormData();
      formData.append('image', image.file);
      const extraMetadata = ImageModel(formDataParams);
      Object.keys(extraMetadata).forEach((paramKey) => {
        formData.append(`${paramKey}`, extraMetadata[paramKey]);
      });
      if (typeof storeRequest === 'function') {
        storeImage(formData).then(({ isOk }) => {
          const message = isOk ? 'Image uploaded successfully' : 'An unexpected error occurred';
          console.log(message);
          onMessage(message);
        });
      }
    };

    const handleOnError = (errors, files) => {
      console.log(errors);
      const messages = [];
      if (errors.maxFileSize) messages.push(`La imagen excede los ${maxSize}kB!`);
      if (errors.acceptType) messages.push(`El formato ${files[0].file.type} no es aceptado`);
      if (errors.maxNumber) messages.push('El número máximo de imágenes se ha superado');
      if (errors.resolution) messages.push(`La imagen excede la resolución permitida`);
      onMessage(messages.join(', '));
      console.warn(messages.join(', '));
    };

    const handleDelete = () => {
      if (typeof deleteRequest === 'function') {
        deleteImage(imageObject)
          .then(({ isOk }) => {
            const message = isOk ? 'Image deleted successfully' : 'An unexpected error occurred';
            console.log(message);
            onMessage(message);
            if (isOk) {
              handleClearAll();
              onDeleteUpload();
            }
          })
          .catch((err) => {
            console.log(err);
          });
        return;
      }
      handleClearAll();
      onDeleteUpload();
    };

    const handleResponseRequest = (reqRes) => {
      if (reqRes === undefined || statusStore !== 'SUCCESS') {
        setWasSavedToServer(false);
        return;
      }
      setImageObject(reqRes);
      setWasSavedToServer(true);
    };

    const handleImageError = (e) => {
      e.target.onerror = null;
      const fallback = document.createElement('div');
      fallback.style.cssText =
        'height:100%; width:100%; display:flex; align-items:center; justify-content:center; color:#888; font-style:italic;';
      fallback.textContent = fallbackMessage;
      e.target.replaceWith(fallback);
    };

    useEffect(() => {
      if (imageObject === undefined) return;
      onAfterUpload(imageObject);
      if (onDirty) onDirty(!_.isEqual(imageObject, setImage));
      const reference = _.merge({}, getDefaults(), imageEqualsTo);
      setImageName(imageObject[reference.title]);
      setImagePath(imageObject[reference.path]);
    }, [imageObject]);

    useEffect(() => {
      handleResponseRequest(responseStore);
    }, [responseStore, statusStore]);

    useEffect(() => {
      return () => {
        handleClearAll();
      };
    }, []);

    useEffect(() => {
      if (canvasImage === undefined || canvasImage.file === '') return;
      handleUpload(canvasImage);
    }, [canvasImage]);

    useImperativeHandle(ref, () => ({
      handleClearAll,
    }));

    return (
      <div className="w-full relative">
        <Card variant="outlined" className="p-0 border-dashed rounded-lg border-2">
          <CardContent
            className="p-0 m-0 overflow-hidden flex justify-center items-center"
            style={{ padding: 0 }}
          >
            <ImageUploading
              maxFileSize={maxSize * 1024}
              className="p-0"
              multiple={false}
              value={images}
              onChange={handleOnChange}
              onError={(errors, files) => handleOnError(errors, files)}
              dataURLKey="data_url"
              acceptType={acceptType}
              style={{ padding: 0, margin: 0 }}
            >
              {({
                imageList,
                onImageUpload,
                onImageRemoveAll,
                onImageUpdate,
                onImageRemove,
                isDragging,
                dragProps,
              }) => (
                <>
                  <Box
                    className="flex flex-col justify-center items-center px-24 pt-16 pb-0 m-0"
                    sx={{ minHeight: '24rem', cursor: 'grab' }}
                  >
                    {!isLoadingStore && (
                      <div className="pb-16">
                        {isMobile ? (
                          <Typography>Seleccione una opción</Typography>
                        ) : (
                          <Typography className="truncate clamp-line-2">
                            Arrastre una imagen aquí o bien seleccione una opción
                          </Typography>
                        )}
                      </div>
                    )}
                    {isLoadingStore ? (
                      <CircularProgress />
                    ) : (
                      <div className="flex flex-col h-full flex-auto justify-around items-center flex-wrap">
                        {wasSavedToServer ? (
                          <>
                            <Box
                              className="flex justify-center"
                              sx={{ height: '16rem', width: '24rem' }}
                            >
                              <img
                                style={{ backgroundSize: 'contain', height: '100%', width: 'auto' }}
                                alt={imageName || 'Uploaded by user for app'}
                                src={`${baseUri || ''}${baseUri ? '/' : ''}${imagePath}`}
                                onError={handleImageError}
                              />
                            </Box>
                            {imageName && (
                              <div className="mt-4 flex flex-wrap flex-row">
                                <Typography variant="caption" display="block" gutterBottom>
                                  {imageName}
                                </Typography>
                              </div>
                            )}
                          </>
                        ) : (
                          <AddPhotoAlternateIcon
                            sx={{
                              width: '8rem',
                              height: '8rem',
                              color: (_theme) => `${_theme.palette.primary.light}C8`,
                            }}
                          />
                        )}
                        {wasSavedToServer ? (
                          <div className="mt-8 flex flex-row justify-center items-center flex-wrap mb-24">
                            {!replaceOnly && (
                              <Button
                                size="small"
                                className="sm:mr-8 z-50"
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteForeverOutlinedIcon />}
                                onClick={handleDelete}
                              >
                                Eliminar imagen
                              </Button>
                            )}
                            <Button
                              onClick={(ev) => {
                                ev.preventDefault();
                                onImageUpload();
                              }}
                              size="small"
                              className="sm:ml-8 z-50"
                              variant="outlined"
                              color="primary"
                              startIcon={<CachedIcon />}
                            >
                              Subir otra imagen
                            </Button>
                          </div>
                        ) : (
                          <div
                            className="mt-8 flex flex-row justify-center items-center flex-wrap mb-24"
                            style={{ zIndex: 1000 }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              className="mx-16"
                              onClick={() => onImageUpload()}
                            >
                              Subir desde computadora
                            </Button>
                            {hasGallery && (
                              <Button
                                className="mx-8 mt-8 md:mt-0"
                                variant="outlined"
                                size="small"
                                startIcon={<FileUploadOutlinedIcon />}
                                // onClick={() => onShowGallery()}
                              >
                                Seleccionar desde galería
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Box>
                  {!isLoadingStore && (
                    <Box
                      {...dragProps}
                      className="absolute top-0 left-0 rounded-lg"
                      sx={{
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        borderColor: (_theme) => `${_theme.palette.primary.light}C8`,
                        backgroundColor: isDragging
                          ? (_theme) => `${_theme.palette.secondary.main}2A`
                          : undefined,
                      }}
                    />
                  )}
                </>
              )}
            </ImageUploading>
          </CardContent>
        </Card>
      </div>
    );
  }
);

ImagePicker.propTypes = {
  hasGallery: PropTypes.bool,
  onAfterUpload: PropTypes.func,
  whereUpload: PropTypes.func,
  adminMode: PropTypes.bool,
};
